import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Image, Dimensions, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useNavigation } from '@react-navigation/native';
import { getData } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import Skeleton from '../common/Skeleton';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const VIBES = ['All', 'Skill Up', 'Live Talks', 'Seminar', 'Meetups'];

const EventRadar = () => {
  const [events, setEvents] = useState([]);
  const [activeVibe, setActiveVibe] = useState('All');
  const [loading, setLoading] = useState(true);
  const navigation = useNavigation();

  useEffect(() => {
    fetchRadar();
  }, [activeVibe]);

  const fetchRadar = async () => {
    try {
      setLoading(true);
      const data = await getData(`/events/radar?vibe=${activeVibe}`);
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching radar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventPress = (eventId) => {
     navigation.navigate('EventDetails', { eventId });
  };

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.vibeScroll}>
        {VIBES.map((v) => (
          <TouchableOpacity 
             key={v}
             style={[styles.vibeChip, activeVibe === v && styles.activeVibeChip]}
             onPress={() => setActiveVibe(v)}
          >
             <Text style={[styles.vibeText, activeVibe === v && styles.activeVibeText]}>{v}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false} 
        contentContainerStyle={styles.eventsScrollContent}
        snapToInterval={SCREEN_WIDTH * 0.85 + 15}
        decelerationRate="fast"
      >
         {loading ? (
            <View style={{ flexDirection: 'row', gap: 15 }}>
               <Skeleton width={SCREEN_WIDTH * 0.85} height={200} borderRadius={16} />
               <Skeleton width={SCREEN_WIDTH * 0.85} height={200} borderRadius={16} />
            </View>
         ) : events.length === 0 ? (
            <View style={styles.emptyState}>
                <Ionicons name="planet-outline" size={28} color="#9CA3AF" />
                <Text style={{color: '#9CA3AF', marginTop: 12}}>No events found for this vibe</Text>
            </View>
         ) : (
            events.map(event => {
                const date = new Date(event.event_date);
                return (
                    <TouchableOpacity 
                       key={event.id}
                       style={styles.fullCard}
                       onPress={() => handleEventPress(event.id)}
                       activeOpacity={0.9}
                    >
                        <Image 
                           source={{ uri: getImageUrl(event.image_url) || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800' }} 
                           style={styles.cardImage} 
                        />
                        <View style={styles.cardOverlay}>
                            <View style={styles.pointsBadgeBig}>
                                <Text style={styles.pointsText}>+{event.points_reward} XP</Text>
                            </View>
                        </View>
                        {(() => {
                          const now = new Date();
                          const start = event.start_time ? new Date(event.start_time) : new Date(event.event_date);
                          const end = event.end_time ? new Date(event.end_time) : new Date(new Date(event.event_date).getTime() + 2 * 60 * 60 * 1000); // fallback to 2h duration
                          
                          if (now > end) {
                            return (
                              <View style={styles.radarCompletedBadge}>
                                <Text style={styles.radarCompletedText}>COMPLETED</Text>
                              </View>
                            );
                          } else if (now >= start && now <= end) {
                            return (
                              <View style={[styles.radarCompletedBadge, { backgroundColor: '#10B981' }]}>
                                <Text style={styles.radarCompletedText}>ONGOING</Text>
                              </View>
                            );
                          }
                          return null;
                        })()}
                        <View style={styles.cardContent}>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <Text style={styles.cardDate}>
                                   {date.toLocaleDateString('en-US', {month:'short', day:'numeric'})} • {event.type === 'virtual' ? 'Virtual' : 'In Person'}
                                </Text>
                            </View>
                            <Text style={styles.cardTitle} numberOfLines={1}>{event.title}</Text>
                            <Text style={styles.cardDesc} numberOfLines={2}>{event.description}</Text>
                        </View>
                    </TouchableOpacity>
                );
            })
         )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 0,
    marginTop: 10,
    marginBottom: 20,
  },
  vibeScroll: {
    paddingHorizontal: 20,
    marginBottom: 15,
  },
  vibeChip: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  activeVibeChip: {
    backgroundColor: '#6366F1',
    borderColor: '#4F46E5',
  },
  vibeText: {
    color: '#6B7280',
    fontWeight: '600',
    fontSize: 12,
  },
  activeVibeText: {
    color: '#FFFFFF',
  },
  eventsScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    width: SCREEN_WIDTH - 40,
    height: 200,
    backgroundColor: '#F9FAFB',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'dashed',
  },
  fullCard: {
    width: SCREEN_WIDTH * 0.82,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    marginRight: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 6,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F3F4F6',
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
    height: 110,
    backgroundColor: '#E5E7EB',
  },
  cardOverlay: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  pointsBadgeBig: {
    backgroundColor: '#FDE68A',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#FBBF24',
  },
  pointsText: {
    color: '#92400E',
    fontWeight: '900',
    fontSize: 10,
  },
  cardContent: {
    padding: 10,
  },
  cardDate: {
    color: '#6366F1',
    fontWeight: '700',
    fontSize: 9,
  },
  cardTitle: {
    color: '#111827',
    fontWeight: '700',
    marginBottom: 3,
    fontSize: 13,
  },
  cardDesc: {
    color: '#6B7280',
    fontSize: 10,
    lineHeight: 14,
  },
  radarCompletedBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: 'rgba(16, 185, 129, 0.95)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    zIndex: 10,
  },
  radarCompletedText: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: 10,
  }
});

export default EventRadar;
